import InputV2 from "@appComponents/InputV2";
import { Checkbox, Input, Modal, notification } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import TickCircleGreyIcon from "../../icons/tick-circle-grey.svg";
import TickCircleGreenIcon from "../../icons/tick-circle-green.svg";
import { Button } from "../Button";
import { use2FAFieldLogic } from "../../hooks/use2FAFieldLogic";
import ProfileService from "@services/Profile.service";
import { TAccountData } from "../../IAccountPage";
import { useMediaQuery } from "react-responsive";
import { useAccountPageContext } from "../../AcountPage.context";
import { DESKTOP_WIDTH } from "@bundles/consts";

const progressColors = [
  "bg-red-500",
  "bg-orange-400",
  "bg-yellow-400",
  "bg-green-400",
  "bg-green-600",
];

const strengthLabels = ["Very weak", "Weak", "Normal", "Strong", "Very strong"];

const passwordValidationRules = [
  { label: "One lowercase character", regex: /[a-z]/ },
  { label: "One uppercase character", regex: /[A-Z]/ },
  { label: "One number", regex: /\d/ },
  { label: "One special character", regex: /[!@#$%^&*(),.?":{}|<>]/ },
  { label: "6 characters minimum", regex: /.{6,}/ },
];

type ChangePasswordModalProps = {
  open?: boolean;
  onClose?: () => void;
  refetchSecuritySessions: () => void;
};

export const ChangePasswordModal = ({
  open,
  onClose,
  refetchSecuritySessions,
}: ChangePasswordModalProps) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [logoutOtherDevices, setLogoutOtherDevices] = useState(false);

  const { is2FAEnabled } = useAccountPageContext();

  const isMobile = useMediaQuery({ maxWidth: DESKTOP_WIDTH });

  const {
    inputRefs: twoFAInputRefs,
    pin: twoFAPin,
    handleChange: twoFAHandleChange,
    handleKeyDown: twoFAHandleKeyDown,
    handleClearPin: twoFAHandleClearPin,
    handlePaste: twoFAHandlePaste,
  } = use2FAFieldLogic();

  const isButtonEnabled = useMemo(() => {
    if (is2FAEnabled && twoFAPin.join("").length < 6) {
      return false;
    }

    return (
      !!currentPassword &&
      !!newPassword &&
      !!confirmNewPassword &&
      newPassword === confirmNewPassword
    );
  }, [currentPassword, newPassword, confirmNewPassword, twoFAPin]);

  const passedConditions = passwordValidationRules.filter((rule) =>
    rule.regex.test(newPassword)
  ).length;

  const handleOnSubmit = () => {
    setErrorMessage("");
    twoFAHandleClearPin();

    const body: any = {
      old_password: currentPassword,
      new_password: newPassword,
      password_confirmation: confirmNewPassword,
      otp_code: twoFAPin.join(""),
    };

    if (logoutOtherDevices) {
      body.log_out_of_other_devices = true;
    }

    ProfileService.changePassword({
      data: body,
    } as unknown as TAccountData)
      .then(() => {
        twoFAHandleClearPin();
        notification.success({
          message: "Your password has been successfully changed",
        });
        refetchSecuritySessions();
        onClose();
      })
      .catch((e) => {
        twoFAHandleClearPin();
        if (e.response.status === 422) {
          setErrorMessage("Current password is incorrect");
        }
      });
  };

  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isButtonEnabled) {
      handleOnSubmit();
    }
  };

  const handleTwoFAEnterKeyPress = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    twoFAHandleKeyDown(index, e);
    if (index === 5 && e.key === "Enter" && isButtonEnabled) {
      handleOnSubmit();
    }
  };

  useEffect(() => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setErrorMessage("");
    twoFAHandleClearPin();
    setLogoutOtherDevices(false);
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={isMobile ? "100%" : "70%"}
      className="tailwind-2fa-modal tailwind"
    >
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-8">Change password</h2>

        <InputV2
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          label="Current password"
          placeholder="Type here"
          onKeyDown={handleEnterKeyPress}
          shouldShowIconBeVisible={open}
        />

        <div className="mt-8">
          <InputV2
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            label="New password"
            placeholder="Type here"
            onKeyDown={handleEnterKeyPress}
            shouldShowIconBeVisible={open}
          />

          <div className="mt-4">
            <div className="flex gap-2 items-center">
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className={classNames(
                    "h-2 flex-1 rounded-full transition-all",
                    index < passedConditions
                      ? progressColors[passedConditions - 1]
                      : "bg-gray-300"
                  )}
                ></div>
              ))}

              <p className="text-xs font-medium text-grey-lightest mb-0 mt-0">
                {strengthLabels[passedConditions - 1]}
              </p>
            </div>
          </div>

          <div
            className={classNames(
              "rounded-[10px] bg-grey-light p-4 mt-4 lg:grid lg:grid-cols-11 lg:gap-4"
            )}
          >
            {passwordValidationRules.map((rule, index) => {
              const isValid = rule.regex.test(newPassword);
              return (
                <div
                  key={index}
                  className={classNames(
                    "flex items-center gap-2",
                    index === 2 ? "col-span-3" : "col-span-4",
                    isValid ? "text-[#23B664]" : "text-grey-lightest"
                  )}
                >
                  {isValid ? <TickCircleGreenIcon /> : <TickCircleGreyIcon />}
                  {rule.label}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-8">
          <InputV2
            type="password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            label="Confirm new password"
            placeholder="Type here"
            onKeyDown={handleEnterKeyPress}
            shouldShowIconBeVisible={open}
          />
        </div>

        <div className="mt-8">
          <Checkbox
            checked={logoutOtherDevices}
            onChange={() => setLogoutOtherDevices((prev) => !prev)}
          >
            <div className="normal-case ml-[2px] mt-[4px]">
              Log out of other devices
            </div>
          </Checkbox>
        </div>

        {is2FAEnabled && (
          <div className="flex flex-col gap-2 mt-8">
            <div className="text-[16px] font-semibold">
              Enter a valid 2FA token to unlock access
            </div>
            <div className="flex gap-2">
              {twoFAPin.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (twoFAInputRefs.current[index] = el)}
                  value={digit}
                  maxLength={1}
                  onChange={(e) => twoFAHandleChange(index, e.target.value)}
                  onKeyDown={(e) => handleTwoFAEnterKeyPress(index, e)}
                  onPaste={twoFAHandlePaste}
                  className="text-center text-lg font-semibold border border-grey-lighter tailwind-2fa-input"
                />
              ))}
            </div>
          </div>
        )}

        {!!errorMessage && (
          <div className="text-[#E02020] mt-8">{errorMessage}</div>
        )}

        <Button
          disabled={!isButtonEnabled}
          isPrimary
          className="w-full mt-[40px]"
          onClick={handleOnSubmit}
        >
          Submit
        </Button>
      </div>
    </Modal>
  );
};
